import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { useToast } from '../../contexts/ToastContext';
import { Search, Trash2, Printer } from 'lucide-react';
import Modal from '../Modal';

interface CustomLabelModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface SelectedItem {
    sku: string;
    title: string;
    quantity: number;
}

export default function CustomLabelModal({ isOpen, onClose }: CustomLabelModalProps) {
    const { api } = useApi();
    const { showSuccess, showError } = useToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);

    const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setSearchResults([]);
            setSelectedItems([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (searchTerm.length >= 3) {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => {
                searchProducts(searchTerm);
            }, 500);
        } else {
            setSearchResults([]);
        }
        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
    }, [searchTerm]);

    const searchProducts = async (term: string) => {
        try {
            setIsSearching(true);
            // Reusar el endpoint de búsqueda de productos del 3PL
            const response = await api.get(`/3pl/products/search?query=${encodeURIComponent(term)}`);
            if (response.data && response.data.variants) {
                setSearchResults(response.data.variants);
            } else {
                setSearchResults([]);
            }
        } catch (err) {
            console.error('Error searching products', err);
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddItem = (product: any) => {
        const defaultTitle = product.title || product.name || product.nombre_producto || '';
        const sku = product.sku || product.cod_pieza || '';

        if (!sku) {
            showError('El producto seleccionado no tiene un SKU válido');
            return;
        }

        if (selectedItems.some(i => i.sku === sku)) {
            showError('Este producto ya está en la lista');
            return;
        }

        setSelectedItems([...selectedItems, {
            sku: sku,
            title: defaultTitle,
            quantity: 1
        }]);

        setSearchTerm('');
        setSearchResults([]);
    };

    const handleUpdateQuantity = (sku: string, quantity: number) => {
        setSelectedItems(items => items.map(item =>
            item.sku === sku ? { ...item, quantity: Math.max(1, quantity) } : item
        ));
    };

    const handleRemoveItem = (sku: string) => {
        setSelectedItems(items => items.filter(item => item.sku !== sku));
    };

    const clearAll = () => {
        setSelectedItems([]);
    };

    const generatePDF = async () => {
        if (selectedItems.length === 0) return;

        try {
            setIsGenerating(true);
            showSuccess('Generando PDF de etiquetas...');

            const payload = selectedItems.map(item => ({
                sku: item.sku,
                quantity: item.quantity,
                title: item.title
            }));

            const response = await api.post('/3pl/labels/custom/pdf', payload, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                };
            } else {
                const link = document.createElement('a');
                link.href = url;
                link.download = `etiquetas-custom-${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showSuccess('PDF descargado al no poder abrir vista previa.');
            }

        } catch (err: any) {
            console.error('Error generating PDF', err);
            showError(err.response?.data?.error || 'Error al generar etiquetas');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Generar Etiquetas de Producto"
            size="large"
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', width: '100%' }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={generatePDF}
                        disabled={selectedItems.length === 0 || isGenerating}
                    >
                        <Printer size={18} style={{ marginRight: '0.5rem' }} />
                        {isGenerating ? 'Generando...' : 'Generar PDF'}
                    </button>
                </div>
            }
        >
            <div className="modal-body" style={{ padding: 0 }}>
                <div style={{ marginBottom: '1.5rem' }}>
                    <label className="form-label">Buscar Producto (SKU o Nombre)</label>
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', padding: '0.5rem 1rem', border: '1px solid var(--border-color)' }}>
                            <Search size={18} style={{ color: 'var(--text-secondary)', marginRight: '0.5rem' }} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Ej. BRA-PYC1..."
                                style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', color: 'var(--text-primary)' }}
                            />
                        </div>

                        {/* Dropdown de resultados */}
                        {searchTerm.length >= 3 && (
                            <div style={{
                                position: 'absolute',
                                top: '100%', left: 0, right: 0,
                                backgroundColor: 'white',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                zIndex: 10,
                                marginTop: '0.25rem',
                                maxHeight: '300px',
                                overflowY: 'auto'
                            }}>
                                {isSearching ? (
                                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Buscando...</div>
                                ) : searchResults.length > 0 ? (
                                    searchResults.map((product, idx) => (
                                        <div
                                            key={idx}
                                            style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                                            onClick={() => handleAddItem(product)}
                                        >
                                            <div style={{ fontWeight: 'bold' }}>{product.sku || product.cod_pieza}</div>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{product.title || product.name || product.nombre_producto}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No se encontraron productos</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="table-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Artículos a imprimir</h3>
                        {selectedItems.length > 0 && (
                            <button
                                className="btn btn-secondary"
                                onClick={clearAll}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                            >
                                Limpiar lista
                            </button>
                        )}
                    </div>

                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>SKU</th>
                                <th>Nombre</th>
                                <th style={{ width: '100px', textAlign: 'center' }}>Cantidad</th>
                                <th style={{ width: '60px', textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedItems.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                        Busca y selecciona productos para añadir a la lista.
                                    </td>
                                </tr>
                            ) : (
                                selectedItems.map((item) => (
                                    <tr key={item.sku}>
                                        <td style={{ fontWeight: '500' }}>{item.sku}</td>
                                        <td>{item.title}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => handleUpdateQuantity(item.sku, parseInt(e.target.value) || 1)}
                                                className="form-control"
                                                style={{ width: '60px', textAlign: 'center', padding: '0.25rem' }}
                                            />
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                className="btn-icon"
                                                onClick={() => handleRemoveItem(item.sku)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={18} style={{ color: 'var(--error)' }} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
}
